import { useState, useEffect } from 'react'
import { X, Save } from 'lucide-react'
import { useOrganizador } from '../../contexts/OrganizadorContext'
import FaixasQtdVendaEditor from '../comissoes/FaixasQtdVendaEditor'

export default function EditarComissaoModal({ isOpen, onClose, onSave, comissao, loading = false }) {
  const { organizadorAtivo } = useOrganizador()
  const [tipoComissao, setTipoComissao] = useState('padrao')
  const [formData, setFormData] = useState({
    nome: '',
    tipo_comissao: 'padrao',
    comissao_venda: '',
    comissao_compra: '',
    comissao_defesa: '',
    // Cenário 1: Ambos sócios
    ambos_socios_comprador: '',
    ambos_socios_vendedor: '',
    ambos_socios_defesa: '',
    // Cenário 2: Só comprador sócio
    comprador_socio_comprador: '',
    comprador_socio_vendedor: '',
    comprador_socio_defesa: '',
    // Cenário 3: Só vendedor sócio
    vendedor_socio_comprador: '',
    vendedor_socio_vendedor: '',
    vendedor_socio_defesa: ''
  })
  
  const [faixas, setFaixas] = useState([{ qtd_min: 1, qtd_max: '', percentual: '' }])
  const usaComissaoPorQtd = organizadorAtivo?.usa_comissao_por_qtd || organizadorAtivo?.produtos_premium?.includes('comissao_por_qtd')
  const usaComissaoSocios = organizadorAtivo?.usa_comissao_socios || organizadorAtivo?.produtos_premium?.includes('comissao_socios')

  useEffect(() => {
    if (comissao) {
      const tipo = comissao.tipo_comissao || 'padrao'
      setTipoComissao(tipo)
      setFaixas(comissao.faixas_qtd_venda?.length
        ? comissao.faixas_qtd_venda.map(f => ({ qtd_min: f.qtd_min, qtd_max: f.qtd_max ?? '', percentual: f.percentual }))
        : [{ qtd_min: 1, qtd_max: '', percentual: '' }])
      setFormData({
        nome: comissao.nome || '',
        tipo_comissao: tipo,
        comissao_venda: comissao.comissao_venda || '',
        comissao_compra: comissao.comissao_compra || '',
        comissao_defesa: comissao.comissao_defesa || '',
        ambos_socios_comprador: comissao.ambos_socios_comprador || '',
        ambos_socios_vendedor: comissao.ambos_socios_vendedor || '',
        ambos_socios_defesa: comissao.ambos_socios_defesa || '',
        comprador_socio_comprador: comissao.comprador_socio_comprador || '',
        comprador_socio_vendedor: comissao.comprador_socio_vendedor || '',
        comprador_socio_defesa: comissao.comprador_socio_defesa || '',
        vendedor_socio_comprador: comissao.vendedor_socio_comprador || '',
        vendedor_socio_vendedor: comissao.vendedor_socio_vendedor || '',
        vendedor_socio_defesa: comissao.vendedor_socio_defesa || ''
      })
    }
  }, [comissao])

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    const data = {
      nome: formData.nome,
      tipo_comissao: tipoComissao,
      comissao_venda: parseFloat(formData.comissao_venda) || 0,
      comissao_compra: parseFloat(formData.comissao_compra) || 0,
      comissao_defesa: parseFloat(formData.comissao_defesa) || 0
    }

    // Comissão por quantidade: envia a tabela; a comissão de venda base vira o percentual da 1a faixa
    if (tipoComissao === 'por_qtd') {
      data.faixas_qtd_venda = faixas.map(f => ({
        qtd_min: parseInt(f.qtd_min, 10),
        qtd_max: f.qtd_max === '' || f.qtd_max == null ? null : parseInt(f.qtd_max, 10),
        percentual: parseFloat(f.percentual) || 0,
      }))
      data.comissao_venda = data.faixas_qtd_venda[0]?.percentual || 0
    } else {
      data.faixas_qtd_venda = null
    }

    // Se for comissão por sócios, incluir os cenários
    if (tipoComissao === 'socios') {
      data.ambos_socios_comprador = parseFloat(formData.ambos_socios_comprador) || 0
      data.ambos_socios_vendedor = parseFloat(formData.ambos_socios_vendedor) || 0
      data.ambos_socios_defesa = parseFloat(formData.ambos_socios_defesa) || 0
      data.comprador_socio_comprador = parseFloat(formData.comprador_socio_comprador) || 0
      data.comprador_socio_vendedor = parseFloat(formData.comprador_socio_vendedor) || 0
      data.comprador_socio_defesa = parseFloat(formData.comprador_socio_defesa) || 0
      data.vendedor_socio_comprador = parseFloat(formData.vendedor_socio_comprador) || 0
      data.vendedor_socio_vendedor = parseFloat(formData.vendedor_socio_vendedor) || 0
      data.vendedor_socio_defesa = parseFloat(formData.vendedor_socio_defesa) || 0
    }
    
    await onSave(data)
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Editar Comissão</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 max-h-96 overflow-y-auto">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nome *
            </label>
            <input
              type="text"
              required
              value={formData.nome}
              onChange={(e) => setFormData({...formData, nome: e.target.value})}
              className="input-rematei"
              placeholder="Ex: Comissão Padrão"
            />
          </div>

          {(usaComissaoSocios || usaComissaoPorQtd) && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tipo de Comissão
              </label>
              <div className="flex space-x-4">
                <label className="flex items-center">
                  <input
                    type="radio"
                    value="padrao"
                    checked={tipoComissao === 'padrao'}
                    onChange={(e) => setTipoComissao(e.target.value)}
                    className="mr-2"
                  />
                  Padrão
                </label>
                {usaComissaoSocios && (
                <label className="flex items-center">
                  <input
                    type="radio"
                    value="socios"
                    checked={tipoComissao === 'socios'}
                    onChange={(e) => setTipoComissao(e.target.value)}
                    className="mr-2"
                  />
                  Comissão por Sócios
                </label>
                )}
                {usaComissaoPorQtd && (
                <label className="flex items-center">
                  <input
                    type="radio"
                    value="por_qtd"
                    checked={tipoComissao === 'por_qtd'}
                    onChange={(e) => setTipoComissao(e.target.value)}
                    className="mr-2"
                  />
                  Por Quantidade de Animais
                </label>
                )}
              </div>
            </div>
          )}

          {tipoComissao === 'por_qtd' ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Comprador (%)</label>
                  <input type="number" step="0.01" min="0" max="100" value={formData.comissao_compra}
                    onChange={(e) => setFormData({...formData, comissao_compra: e.target.value})}
                    className="input-rematei" placeholder="3.00" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Defesa (%)</label>
                  <input type="number" step="0.01" min="0" max="100" value={formData.comissao_defesa}
                    onChange={(e) => setFormData({...formData, comissao_defesa: e.target.value})}
                    className="input-rematei" placeholder="1.00" />
                </div>
              </div>
              <FaixasQtdVendaEditor faixas={faixas} onChange={setFaixas} />
            </div>
          ) : tipoComissao === 'socios' ? (
            <div className="space-y-4">
              {/* Cenário 1: Ambos sócios */}
              <div className="border border-green-200 rounded-lg p-4 bg-green-50">
                <h4 className="font-medium text-green-800 mb-2">👥 Cenário 1: Ambos Sócios</h4>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Comprador (%)</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      value={formData.ambos_socios_comprador}
                      onChange={(e) => setFormData({...formData, ambos_socios_comprador: e.target.value})}
                      className="input-rematei text-sm"
                      placeholder="1.5"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Vendedor (%)</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      value={formData.ambos_socios_vendedor}
                      onChange={(e) => setFormData({...formData, ambos_socios_vendedor: e.target.value})}
                      className="input-rematei text-sm"
                      placeholder="2.0"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Defesa (%)</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      value={formData.ambos_socios_defesa}
                      onChange={(e) => setFormData({...formData, ambos_socios_defesa: e.target.value})}
                      className="input-rematei text-sm"
                      placeholder="0.5"
                    />
                  </div>
                </div>
              </div>

              {/* Cenário 2: Só comprador sócio */}
              <div className="border border-blue-200 rounded-lg p-4 bg-blue-50">
                <h4 className="font-medium text-blue-800 mb-2">👤 Cenário 2: Só Comprador Sócio</h4>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Comprador (%)</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      value={formData.comprador_socio_comprador}
                      onChange={(e) => setFormData({...formData, comprador_socio_comprador: e.target.value})}
                      className="input-rematei text-sm"
                      placeholder="2.0"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Vendedor (%)</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      value={formData.comprador_socio_vendedor}
                      onChange={(e) => setFormData({...formData, comprador_socio_vendedor: e.target.value})}
                      className="input-rematei text-sm"
                      placeholder="3.0"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Defesa (%)</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      value={formData.comprador_socio_defesa}
                      onChange={(e) => setFormData({...formData, comprador_socio_defesa: e.target.value})}
                      className="input-rematei text-sm"
                      placeholder="1.0"
                    />
                  </div>
                </div>
              </div>

              {/* Cenário 3: Só vendedor sócio */}
              <div className="border border-purple-200 rounded-lg p-4 bg-purple-50">
                <h4 className="font-medium text-purple-800 mb-2">👤 Cenário 3: Só Vendedor Sócio</h4>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Comprador (%)</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      value={formData.vendedor_socio_comprador}
                      onChange={(e) => setFormData({...formData, vendedor_socio_comprador: e.target.value})}
                      className="input-rematei text-sm"
                      placeholder="2.5"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Vendedor (%)</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      value={formData.vendedor_socio_vendedor}
                      onChange={(e) => setFormData({...formData, vendedor_socio_vendedor: e.target.value})}
                      className="input-rematei text-sm"
                      placeholder="2.5"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Defesa (%)</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      value={formData.vendedor_socio_defesa}
                      onChange={(e) => setFormData({...formData, vendedor_socio_defesa: e.target.value})}
                      className="input-rematei text-sm"
                      placeholder="1.0"
                    />
                  </div>
                </div>
              </div>

              {/* Cenário 4: Nenhum sócio */}
              <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                <h4 className="font-medium text-gray-800 mb-2">❌ Cenário 4: Nenhum Sócio</h4>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Comprador (%)</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      value={formData.comissao_compra}
                      onChange={(e) => setFormData({...formData, comissao_compra: e.target.value})}
                      className="input-rematei text-sm"
                      placeholder="3.0"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Vendedor (%)</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      value={formData.comissao_venda}
                      onChange={(e) => setFormData({...formData, comissao_venda: e.target.value})}
                      className="input-rematei text-sm"
                      placeholder="4.0"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Defesa (%)</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      value={formData.comissao_defesa}
                      onChange={(e) => setFormData({...formData, comissao_defesa: e.target.value})}
                      className="input-rematei text-sm"
                      placeholder="1.5"
                    />
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Comprador (%)
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={formData.comissao_compra}
                  onChange={(e) => setFormData({...formData, comissao_compra: e.target.value})}
                  className="input-rematei"
                  placeholder="3.00"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Vendedor (%)
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={formData.comissao_venda}
                  onChange={(e) => setFormData({...formData, comissao_venda: e.target.value})}
                  className="input-rematei"
                  placeholder="5.00"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Defesa (%)
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={formData.comissao_defesa}
                  onChange={(e) => setFormData({...formData, comissao_defesa: e.target.value})}
                  className="input-rematei"
                  placeholder="1.00"
                />
              </div>
            </div>
            </>
          )}

          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary flex-1"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="btn-primary flex-1"
            >
              {loading ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Salvar
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}